import { useId } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { cn } from '../lib/cn';
import { springs } from '../lib/motion';
import { useTheme, type ThemePreference } from '../lib/theme';

const OPTIONS: { value: ThemePreference; label: string; icon: typeof Sun }[] = [
  { value: 'light', label: 'Claro', icon: Sun },
  { value: 'dark', label: 'Escuro', icon: Moon },
  { value: 'system', label: 'Automático', icon: Monitor },
];

export interface ThemeToggleProps {
  /** `segmented`: claro / escuro / automático. `icon`: um botão que alterna claro e escuro. */
  variant?: 'segmented' | 'icon';
  /** `on-dark` para superfícies sempre escuras (menu lateral). */
  tone?: 'default' | 'on-dark';
  /** Mostra os nomes ao lado dos ícones (só no segmentado). */
  showLabels?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

/** Troca de tema do aparelho (salva em localStorage 'dezza:theme'). */
export function ThemeToggle({ variant = 'segmented', tone = 'default', showLabels = true, size = 'md', className }: ThemeToggleProps) {
  const { theme, resolvedTheme, setTheme, toggleTheme } = useTheme();
  const layoutId = useId();
  const reduce = useReducedMotion();
  const onDark = tone === 'on-dark';

  if (variant === 'icon') {
    const dark = resolvedTheme === 'dark';
    const Icon = dark ? Moon : Sun;
    return (
      <button
        type="button"
        onClick={toggleTheme}
        aria-label={dark ? 'Usar tema claro' : 'Usar tema escuro'}
        title={dark ? 'Tema escuro (clique para o claro)' : 'Tema claro (clique para o escuro)'}
        className={cn(
          'relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full transition-[background-color,color,scale] duration-150 active:scale-[0.92] motion-reduce:active:scale-100',
          size === 'md' ? 'size-10 [&_svg]:size-5' : 'size-8 [&_svg]:size-4',
          onDark ? 'text-on-dark/90 hover:bg-white/10 hover:text-on-dark' : 'text-ink-2 hover:bg-ink/[0.06] hover:text-ink',
          className,
        )}
      >
        <AnimatePresence initial={false} mode="popLayout">
          <motion.span
            key={dark ? 'moon' : 'sun'}
            className="flex"
            initial={reduce ? { opacity: 0 } : { opacity: 0, rotate: -90, scale: 0.6 }}
            animate={{ opacity: 1, rotate: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, rotate: 90, scale: 0.6 }}
            transition={reduce ? { duration: 0.1 } : springs.snappy}
          >
            <Icon aria-hidden="true" />
          </motion.span>
        </AnimatePresence>
      </button>
    );
  }

  return (
    <div
      role="radiogroup"
      aria-label="Tema"
      className={cn(
        'inline-flex rounded-control p-1 ring-1 ring-inset',
        onDark ? 'bg-white/[0.06] ring-white/10' : 'bg-ink/[0.05] ring-line',
        className,
      )}
    >
      {OPTIONS.map((o) => {
        const active = theme === o.value;
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={active}
            aria-label={showLabels ? undefined : o.label}
            title={showLabels ? undefined : o.label}
            onClick={() => setTheme(o.value)}
            className={cn(
              'relative isolate inline-flex flex-1 items-center justify-center gap-1.5 whitespace-nowrap rounded-[9px] font-semibold transition-colors duration-150',
              size === 'md' ? 'h-9 px-3 text-sm [&_svg]:size-4' : 'h-7 px-2.5 text-xs [&_svg]:size-3.5',
              onDark
                ? active
                  ? 'text-white'
                  : 'text-white/60 hover:text-white'
                : active
                  ? 'text-ink'
                  : 'text-ink-2 hover:text-ink',
            )}
          >
            {active ? (
              <motion.span
                layoutId={`theme-${layoutId}`}
                transition={reduce ? { duration: 0 } : springs.snappy}
                aria-hidden="true"
                className={cn(
                  'absolute inset-0 -z-10 rounded-[9px]',
                  onDark ? 'bg-white/[0.14] ring-1 ring-inset ring-white/10' : 'bg-surface-raised shadow-[0_1px_2px_rgb(14_14_20/0.08),0_0_0_1px_var(--color-line)]',
                )}
              />
            ) : null}
            <Icon aria-hidden="true" />
            {showLabels ? o.label : null}
          </button>
        );
      })}
    </div>
  );
}
